import React from 'react';
import AdminContainer from '../../../../Utility/AdminContainer';
import AdminBox from '../../../../Utility/AdminBox';
import AdminBoxHeadline from '../../../../Utility/AdminBoxHeadline';
import McpTester from './McpTester';
import useMcpConfig from '../../../../../Hooks/useMcpConfig';
import InfoButton from '../../../../Utility/InfoButton';
import { BasicInformationSection, AuthenticationSection, FormControls } from './components/index';
import { RootState, useAppSelector } from '../../../../../store';
import OAuthResetButton from "./components/OAuthResetButton";

function McpConfigPage() {
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const [resetFlag, setResetFlag] = React.useState(false);
    const {
        formData,
        errors,
        isLoading,
        config,
        isNew,
        handleSave,
        handleCancel,
        handleTestConnection,
        updateFormData,
        updateAuthData,
        activeTab,
        setActiveTab,
        isMobile,
        boxHeightClassesStr,
        hasUnsavedChanges,
        handleSyncTools,
    } = useMcpConfig({
        onSaved: () => setResetFlag((prev) => !prev),
    });

    return (
        <div className="admin-container flex relative flex-col">
            <AdminContainer classList="!p-0 xl:!py-0">
                <div className={`h-[calc(var(--app-height)-70px)] xl:h-[calc(var(--app-height)-100px)] flex flex-col`}>
                    <div className="lg:hidden w-full px-6 pt-6">
                        <div className="flex w-full relative">
                            <div
                                style={{ left: `${(100 / 2) * activeTab}%`, width: `${100 / 2}%` }}
                                className="absolute z-[5] bottom-0 h-[2px] bg-primary transition-all duration-300 ease-in-out"
                            ></div>
                            <div className="absolute bottom-0 w-full h-[2px] bg-layout-borderColor transition-all duration-300 ease-in-out"></div>
                            {[
                                i18n.translate({ id: 'tabs.config', sourceName: 'MCP' }),
                                i18n.translate({ id: 'tabs.tester', sourceName: 'MCP' }),
                            ].map((tab, index) => (
                                <div
                                    key={index}
                                    className={`relative z-[10] flex-1 text-center p-2 px-6 cursor-pointer ${
                                        index === activeTab ? 'text-primary' : 'text-layout-textcolor'
                                    }`}
                                    onClick={() => setActiveTab(index as 0 | 1)}
                                >
                                    {tab}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div
                        className={`flex-1 min-h-0 gap-6 grid grid-cols-12 relative overflow-y-auto p-5 xl:p-10 xl:!pb-0 pb-10 xl:pt-5`}
                    >
                        <AdminBox
                            contentLoaded={!isLoading}
                            classString={`bg-transparent p-0 xl:pb-5 mb-0 shadow-none col-span-12 md:col-span-12 lg:col-span-6 ${
                                isMobile ? (activeTab === 0 ? 'block' : 'hidden') : ''
                            }`}
                        >
                            <div className="flex flex-col gap-6">
                                <AdminBoxHeadline
                                    headLine={
                                        isNew
                                            ? i18n.translate({ id: 'form.title.create', sourceName: 'MCP' })
                                            : i18n.translate({ id: 'form.title.edit', sourceName: 'MCP' })
                                    }
                                    subLine={isNew ? undefined : config?.title}
                                />
                                <div className="flex flex-col gap-6">
                                    <AdminBox classString="mb-0 relative">
                                        <InfoButton configKey="mcpBasicInfo" />
                                        <BasicInformationSection
                                            title={formData.title}
                                            description={formData.description}
                                            serverUrl={formData.serverUrl}
                                            transport={formData.transport}
                                            timeout={formData.timeout}
                                            errors={errors}
                                            onChange={updateFormData}
                                        />
                                    </AdminBox>

                                    <AdminBox classString="mb-0 relative">
                                        {config?.identifier && <OAuthResetButton
                                            configId={config?.identifier}
                                            authType={formData.authType}
                                            onAuthDataChange={updateAuthData}
                                        />}
                                        <InfoButton configKey="mcpAuth" />
                                        <AuthenticationSection
                                            configId={config?.identifier}
                                            authType={formData.authType}
                                            authData={formData.authData}
                                            errors={errors}
                                            onAuthTypeChange={(authType) => updateFormData({ authType, authData: {} })}
                                            onAuthDataChange={updateAuthData}
                                            hasUnsavedChanges={hasUnsavedChanges}
                                        />
                                    </AdminBox>

                                    <FormControls
                                        isLoading={isLoading}
                                        isNew={isNew}
                                        hasServerUrl={!!formData.serverUrl}
                                        hasUnsavedChanges={hasUnsavedChanges}
                                        onCancel={handleCancel}
                                        onTest={handleTestConnection}
                                        onSubmit={handleSave}
                                        onSync={() => handleSyncTools(config)}
                                    />
                                </div>
                            </div>
                        </AdminBox>

                        <AdminBox
                            classString={`${
                                !isMobile ? `${boxHeightClassesStr} xl:sticky top-0` : ''
                            } !mb-0 col-span-12 md:col-span-12 lg:col-span-6 ${
                                isMobile ? (activeTab === 1 ? 'block' : 'hidden') : 'lg:block'
                            } shadow-none flex flex-col ${!isMobile ? 'overflow-y-auto' : ''}`}
                        >
                            <div className={`flex-1 min-h-0`}>
                                <McpTester
                                    preselectedConfigIdentifier={config?.identifier || undefined}
                                    resetSignal={resetFlag}
                                />
                            </div>
                        </AdminBox>
                    </div>
                </div>
            </AdminContainer>
        </div>
    );
}

export default McpConfigPage;
